import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageSquare, Send, Search, Phone, Mail, User, Bot, UserCheck,
  RefreshCw, CheckCircle, Clock, MessageCircle, ArrowLeft, Zap,
  Filter, MoreVertical, ChevronRight, Wifi, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

import { API_URL } from '@/config/api';

const CHANNEL_ICONS = {
  sms: MessageSquare,
  email: Mail,
  whatsapp: MessageCircle,
  facebook: MessageCircle,
  instagram: MessageCircle,
  live_chat: MessageCircle,
};

const CHANNEL_COLORS = {
  sms: 'bg-blue-100 text-blue-700',
  email: 'bg-amber-100 text-amber-700',
  whatsapp: 'bg-emerald-100 text-emerald-700',
  facebook: 'bg-indigo-100 text-indigo-700',
  instagram: 'bg-pink-100 text-pink-700',
  live_chat: 'bg-purple-100 text-purple-700',
};

const InboxPage = () => {
  const { user, selectedBusiness, selectedCampaign } = useAuth();
  const businessId = selectedCampaign?.business_id || selectedBusiness?.business_id || user?.business_id;

  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [search, setSearch] = useState('');
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterOwnership, setFilterOwnership] = useState('all');

  // Lead details for selected conversation
  const [selectedLead, setSelectedLead] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 
      'Content-Type': 'application/json', 
      ...(token ? { Authorization: `Bearer ${token}` } : {}) 
    };
  }, []);

  // Sync all messages from GHL
  const syncAllMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/sync-all-messages`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced ${data.synced_messages || 0} messages from GHL`);
        fetchConversations();
      } else {
        toast.error('Failed to sync messages');
      }
    } catch (error) {
      toast.error('Failed to sync messages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all conversations with recent messages
  const fetchConversations = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ business_id: businessId });
      if (selectedCampaign?.campaign_id) {
        params.set('campaign_id', selectedCampaign.campaign_id);
      }
      const res = await fetch(`${API_URL}/api/inbox/conversations?${params}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId, selectedCampaign, authHeaders]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API_URL}/api/conversations/${conversationId}/messages`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [authHeaders]);

  // Fetch lead details
  const fetchLead = useCallback(async (leadId) => {
    try {
      const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedLead(data);
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!businessId) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const connectWS = () => {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsBase = API_URL
        ? API_URL.replace('https://', 'wss://').replace('http://', 'ws://')
        : `${wsProtocol}//${window.location.host}`;
      const ws = new WebSocket(`${wsBase}/api/ws/inbox/${businessId}?token=${token}`);
      
      ws.onopen = () => {
        setWsConnected(true);
        // Send periodic pings to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            // Update messages if viewing the same conversation
            setMessages(prev => {
              const convId = data.message?.conversation_id;
              if (prev.length > 0 && prev[0]?.conversation_id === convId) {
                const exists = prev.some(m => m.message_id === data.message.message_id);
                if (!exists) return [...prev, data.message];
              }
              return prev;
            });
            
            // Update conversation list preview
            if (data.conversation) {
              setConversations(prev => {
                const idx = prev.findIndex(c => c.conversation_id === data.conversation.conversation_id);
                if (idx >= 0) {
                  const updated = [...prev];
                  updated[idx] = { ...updated[idx], ...data.conversation };
                  return updated;
                }
                // New conversation - refetch list
                fetchConversations();
                return prev;
              });
            }
          }
        } catch (e) { /* ignore parse errors */ }
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        // Reconnect after 5 seconds
        reconnectTimerRef.current = setTimeout(connectWS, 5000);
      };
      
      ws.onerror = () => {
        ws.close();
      };
      
      wsRef.current = ws;
    };
    
    connectWS();
    
    // Fallback polling every 60s (in case WS drops)
    const interval = setInterval(fetchConversations, 60000);
    
    return () => {
      clearInterval(interval);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [businessId, fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);
      if (selectedConversation.lead_id) {
        fetchLead(selectedConversation.lead_id);
      }
    }
  }, [selectedConversation, fetchMessages, fetchLead]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !selectedLead) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          lead_id: selectedLead.lead_id,
          channel: selectedConversation.channel,
          content: newMessage.trim(),
          content_type: 'text'
        }),
      });
      
      if (res.ok) {
        const sentMessage = await res.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        toast.success('Message sent');
        
        // Update conversation preview
        setConversations(prev => prev.map(c => 
          c.conversation_id === selectedConversation.conversation_id 
            ? { ...c, last_message_preview: newMessage.slice(0, 100), last_message_at: new Date().toISOString(), last_message_direction: 'outbound' }
            : c
        ));
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle takeover
  const handleTakeover = async () => {
    if (!selectedLead) return;
    try {
      const res = await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}/takeover`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ lead_id: selectedLead.lead_id, reason: 'Inbox takeover' }),
      });
      if (res.ok) {
        toast.success('Lead taken over');
        setSelectedLead(prev => ({ ...prev, human_takeover: true, next_action_owner: 'human' }));
      }
    } catch (error) {
      toast.error('Failed to take over');
    }
  };

  // Handle return to AI
  const handleReturnToAI = async () => {
    if (!selectedLead) return;
    try {
      const res = await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}/return-to-ai`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ lead_id: selectedLead.lead_id }),
      });
      if (res.ok) {
        toast.success('Returned to AI');
        setSelectedLead(prev => ({ ...prev, human_takeover: false, next_action_owner: 'ai' }));
      }
    } catch (error) {
      toast.error('Failed to return to AI');
    }
  };

  // Filter conversations
  const filteredConversations = conversations.filter(c => {
    if (filterChannel !== 'all' && c.channel !== filterChannel) return false;
    if (filterOwnership !== 'all' && c.ownership !== filterOwnership) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return c.lead_name?.toLowerCase().includes(searchLower) || 
             c.last_message_preview?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  return (
    <div className="h-[calc(100vh-120px)] flex bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Conversation List */}
      <div className={`w-full md:w-96 border-r border-slate-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-500" />
              Inbox
            </h2>
            <div className="flex items-center gap-1">
              {wsConnected ? (
                <Badge className="text-[10px] bg-green-100 text-green-700 gap-1" data-testid="ws-status-connected">
                  <Wifi className="w-3 h-3" /> Live
                </Badge>
              ) : (
                <Badge className="text-[10px] bg-slate-100 text-slate-500 gap-1" data-testid="ws-status-disconnected">
                  <WifiOff className="w-3 h-3" /> Offline
                </Badge>
              )}
              <Button size="sm" variant="ghost" onClick={syncAllMessages} disabled={loading} title="Sync from GHL">
                <Zap className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button size="sm" variant="ghost" onClick={fetchConversations} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search conversations..." 
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mt-3">
            <select 
              className="text-xs border rounded-lg px-2 py-1.5 bg-white"
              value={filterChannel}
              onChange={(e) => setFilterChannel(e.target.value)}
            >
              <option value="all">All Channels</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
            </select>
            <select 
              className="text-xs border rounded-lg px-2 py-1.5 bg-white"
              value={filterOwnership}
              onChange={(e) => setFilterOwnership(e.target.value)}
            >
              <option value="all">All</option>
              <option value="ai">AI Handling</option>
              <option value="human">Human Handling</option>
            </select>
          </div>
        </div>

        {/* Conversation List */}
        <ScrollArea className="flex-1">
          {loading && conversations.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <MessageSquare className="w-10 h-10 mb-3 text-slate-200" />
              <p>No conversations yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredConversations.map((conv) => {
                const ChannelIcon = CHANNEL_ICONS[conv.channel] || MessageSquare;
                const isSelected = selectedConversation?.conversation_id === conv.conversation_id;
                
                return (
                  <button
                    key={conv.conversation_id}
                    className={`w-full p-4 text-left transition-colors hover:bg-slate-50 ${isSelected ? 'bg-cyan-50 border-l-2 border-cyan-500' : ''}`}
                    onClick={() => setSelectedConversation(conv)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-medium flex-shrink-0">
                        {conv.lead_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        {/* Name + Time */}
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-slate-900 truncate">{conv.lead_name || 'Unknown'}</span>
                          <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                            {conv.last_message_at ? format(new Date(conv.last_message_at), 'HH:mm') : ''}
                          </span>
                        </div>
                        
                        {/* Preview */}
                        <p className="text-sm text-slate-500 truncate mb-1.5">
                          {conv.last_message_direction === 'outbound' && <span className="text-slate-400">You: </span>}
                          {conv.last_message_preview || 'No messages'}
                        </p>
                        
                        {/* Badges */}
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] ${CHANNEL_COLORS[conv.channel] || 'bg-slate-100 text-slate-600'}`}>
                            <ChannelIcon className="w-3 h-3 mr-1" />
                            {conv.channel}
                          </Badge>
                          {conv.ownership === 'ai' && (
                            <Badge className="text-[10px] bg-purple-100 text-purple-700">
                              <Bot className="w-3 h-3 mr-1" />AI
                            </Badge>
                          )}
                          {conv.unread_count > 0 && (
                            <Badge className="text-[10px] bg-red-500 text-white">{conv.unread_count}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Message View */}
      <div className={`flex-1 flex flex-col ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-100 flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="md:hidden"
                onClick={() => setSelectedConversation(null)}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-medium">
                {selectedLead?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-900 truncate">{selectedLead?.name || 'Loading...'}</h3>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>{selectedLead?.phone || selectedLead?.email || ''}</span>
                  {selectedLead?.ghl_contact_id && (
                    <Badge className="text-[10px] bg-orange-100 text-orange-700">
                      <Zap className="w-3 h-3 mr-1" />GHL
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Takeover Controls */}
              <div className="flex items-center gap-2">
                {selectedLead?.human_takeover ? (
                  <Button size="sm" variant="outline" onClick={handleReturnToAI}>
                    <Bot className="w-4 h-4 mr-1" />
                    Return to AI
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleTakeover} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <UserCheck className="w-4 h-4 mr-1" />
                    Take Over
                  </Button>
                )}
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12 text-slate-400">
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <MessageSquare className="w-10 h-10 mb-3 text-slate-200" />
                  <p>No messages in this conversation</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => {
                    const isOutbound = msg.direction === 'outbound';
                    const isAI = msg.sender_type === 'ai';
                    
                    return (
                      <div 
                        key={msg.message_id} 
                        className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[75%] ${isOutbound ? 'order-2' : 'order-1'}`}>
                          {/* Sender label */}
                          <div className={`text-xs text-slate-400 mb-1 ${isOutbound ? 'text-right' : 'text-left'}`}>
                            {isAI && <Bot className="w-3 h-3 inline mr-1" />}
                            {msg.sender_name || (isOutbound ? 'You' : selectedLead?.name)}
                            {' • '}
                            {format(new Date(msg.created_at), 'HH:mm')}
                          </div>
                          
                          {/* Message bubble */}
                          <div className={`rounded-2xl px-4 py-2.5 ${
                            isOutbound 
                              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white' 
                              : 'bg-slate-100 text-slate-900'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          
                          {/* Status */}
                          {isOutbound && (
                            <div className="text-xs text-slate-400 mt-1 text-right flex items-center justify-end gap-1">
                              {msg.status === 'sent' && <CheckCircle className="w-3 h-3" />}
                              {msg.status === 'delivered' && <CheckCircle className="w-3 h-3 text-blue-500" />}
                              {msg.status === 'failed' && <span className="text-red-500">Failed</span>}
                              {msg.status}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="p-4 border-t border-slate-100">
              <div className="flex gap-2">
                <Textarea
                  placeholder={`Message ${selectedLead?.name || ''}...`}
                  className="min-h-[44px] max-h-32 resize-none"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button 
                  onClick={handleSendMessage} 
                  disabled={!newMessage.trim() || sending}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 px-4"
                >
                  {sending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Press Enter to send • Shift+Enter for new line
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <MessageSquare className="w-16 h-16 mb-4 text-slate-200" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose from the list to view messages</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxPage;
